import { Divider } from '@eventbrite/eds-divider';
import { Icon } from '@eventbrite/eds-icon';
import { Money } from '@eventbrite/eds-iconography';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import {
    RefundPolicyCode,
    RefundPolicyCodeWithoutValidityDays,
} from '../../api/models/refundPolicy';
import { fallbackRefundPolicyValidityDaysByRefundPolicyCode } from '../../models/refundPolicy';
import RebrandingRefundIcon from '../../resources/rebranding-refund-icon';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../utils/rebrand';
import defaultStyles from './RefundPolicy.module.scss';
import rebrandStyles from './RefundPolicy.rebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

const refundPolicyMessageByRefundPolicyCodeWithoutValidityDays: Record<
    RefundPolicyCodeWithoutValidityDays,
    GenericLazyString
> = {
    [RefundPolicyCode.noRefunds]: gettext('No Refunds.'),
    [RefundPolicyCode.notDefined]: gettext(
        'Contact the organizer to request a refund.',
    ),
    [RefundPolicyCode.custom]: gettext('Custom Refund Policy'),
};

const getRefundPolicyMessage = ({
    refundPolicyCode,
    refundPolicyValidityDays,
    isAppRebrandActive,
}: {
    refundPolicyCode: RefundPolicyCode;
    refundPolicyValidityDays: number | null;
    isAppRebrandActive: boolean;
}) => {
    if (!refundPolicyCode)
        return refundPolicyMessageByRefundPolicyCodeWithoutValidityDays[
            RefundPolicyCode.noRefunds
        ];

    const days =
        refundPolicyValidityDays ||
        fallbackRefundPolicyValidityDaysByRefundPolicyCode[refundPolicyCode];

    if (days === null || days === 0) {
        return refundPolicyMessageByRefundPolicyCodeWithoutValidityDays[
            refundPolicyCode as RefundPolicyCodeWithoutValidityDays
        ];
    }

    const validityDays = days.toString();
    const daysString = days > 1 ? 'days' : 'day';
    const openEmphasisTag = !isAppRebrandActive ? '<b>' : '';
    const closeEmphasisTag = !isAppRebrandActive ? '</b>' : '';

    return gettext(
        'Refunds up to %(openEmphasisTag)s%(validityDays)s %(daysString)s%(closeEmphasisTag)s before event.',
        {
            validityDays,
            daysString,
            openEmphasisTag,
            closeEmphasisTag,
        },
    );
};

export const RefundPolicy: FunctionComponent<
    {
        enableRefundFeeRetentionPolicy: boolean;
        showRefundPolicy: boolean;
        isAppRebrandActive: boolean;
    } & (
        | {
              refundPolicyCode: Exclude<
                  RefundPolicyCode,
                  RefundPolicyCodeWithoutValidityDays
              >;
              refundPolicyValidityDays: number;
          }
        | {
              refundPolicyCode: RefundPolicyCodeWithoutValidityDays;
              refundPolicyValidityDays: number | null;
          }
    )
> = ({
    enableRefundFeeRetentionPolicy,
    refundPolicyCode,
    refundPolicyValidityDays,
    showRefundPolicy,
    isAppRebrandActive,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    const eventbriteFeesMessage = gettext("Eventbrite's fee is nonrefundable.");
    const eventbriteFeesMessageComponent =
        enableRefundFeeRetentionPolicy && !isAppRebrandActive ? (
            <div>{eventbriteFeesMessage}</div>
        ) : null;

    const refundPolicyMessage = getRefundPolicyMessage({
        refundPolicyCode,
        refundPolicyValidityDays,
        isAppRebrandActive,
    }).toString();

    const title = isAppRebrandActive
        ? gettext('Refund policy')
        : gettext('Refund Policy');

    const refundPolicy =
        isAppRebrandActive && enableRefundFeeRetentionPolicy
            ? `${refundPolicyMessage} ${eventbriteFeesMessage}`
            : refundPolicyMessage;

    const titleWrapperClassnames = classNames({
        'eds-text-weight--heavy': !isAppRebrandActive,
    });

    const icon = isAppRebrandActive ? (
        <RebrandingRefundIcon />
    ) : (
        <Icon size="xsmall" type={<Money />} color="grey-600" />
    );

    const divider = !isAppRebrandActive ? <Divider /> : null;

    return showRefundPolicy ? (
        <div data-testid="pane-refund-policy">
            {divider}
            <div
                className={classNames(
                    'eds-text-bs',
                    {
                        'eds-l-mar-top-4': !isAppRebrandActive,
                        'eds-l-mar-top-7': isAppRebrandActive,
                    },
                    styles.root,
                )}
            >
                <div className={titleWrapperClassnames}>
                    {icon}
                    <span className={styles.title}>{title}</span>
                </div>
                <div
                    className={styles.messageWrapper}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: refundPolicy,
                    }}
                />
                {eventbriteFeesMessageComponent}
            </div>
        </div>
    ) : null;
};
